import PropTypes from 'prop-types';

import * as React from 'react';
import { cn } from '@/lib/utils';

import Icon from '@/components/atoms/icon';
import Text from '@/components/atoms/text';
import Checkbox from '@/components/molecules/checkbox';
import Link from '@/components/atoms/link';
import Button from '@/components/atoms/button';
import Heading from '@/components/atoms/heading';

const NotificationCard = React.forwardRef(
	(
		{
			type,
			title,
			content,
			checkbox,
			link,
			priBtn,
			priBtnType,
			priBtnVariant,
			priBtnShowIcon,
			priBtnClasses,
			priBtnOnClick,
			secBtn,
			secBtnType,
			secBtnVariant,
			secBtnShowIcon,
			secBtnClasses,
			secBtnOnClick,
			info,
			className,
			titleClassName,
			fullWidth,
			...props
		},
		ref
	) => {
		let iconColour = null;

		if (type === 'info') {
			iconColour = 'text-notificationsInfo';
		} else if (type === 'success') {
			iconColour = 'text-notificationsSuccess';
		} else if (type === 'warning') {
			iconColour = 'text-notificationsWarning';
		} else {
			iconColour = 'text-notificationsError';
		}
		return (
			<div ref={ref} className={cn('flex flex-col gap-6 bg-white p-6 rounded-2xl border border-formOutline', fullWidth ? null : 'w-eis-513', className)} {...props}>
				<div className="flex flex-col gap-2">
					<div className={`flex gap-4 items-center ${iconColour}`}>
						<Icon name={type} className={type === 'info' ? 'w-6 h-6' : ''} />
						<Heading type="h4" className={titleClassName}>
							{title}
						</Heading>
					</div>

					{typeof content === 'string' ? (
						<Text type="small" variant="normal">
							{content}
						</Text>
					) : (
						content
					)}
				</div>

				{checkbox && <Checkbox htmlFor={checkbox.htmlFor} label={checkbox.label} />}

				{info && (
					<div className="flex gap-2">
						<Text type="p" variant="bold">
							{info.name}
						</Text>

						<Text type="p" variant="normal">
							{info.value}
						</Text>
					</div>
				)}

				{link && (
					<Link variant="dark" href={link.href} isExternal={link.isExternal} className="first-letter:uppercase">
						{link.name}
					</Link>
				)}

				{(priBtn || secBtn) && (
					<div className="flex justify-end gap-4">
						{secBtn && (
							<Button
								variant={secBtnVariant}
								type={secBtnType}
								onClick={secBtnOnClick}
								{...(secBtnShowIcon && { icon: 'plus' })}
								className={cn('w-max', secBtnClasses)}
							>
								{secBtn}
							</Button>
						)}

						{priBtn && (
							<Button
								variant={priBtnVariant}
								type={priBtnType}
								onClick={priBtnOnClick}
								{...(priBtnShowIcon && { icon: 'plus' })}
								className={cn('w-max', priBtnClasses)}
							>
								{priBtn}
							</Button>
						)}
					</div>
				)}
			</div>
		);
	}
);

export default NotificationCard;

NotificationCard.propTypes = {
	type: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	content: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.node]).isRequired,
	checkbox: PropTypes.shape({
		htmlFor: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
	}),
	link: PropTypes.shape({
		name: PropTypes.string.isRequired,
		href: PropTypes.string.isRequired,
		isExternal: PropTypes.bool,
	}),
	priBtn: PropTypes.string,
	priBtnType: PropTypes.string,
	priBtnVariant: PropTypes.oneOf([
		'primary',
		'primary-white',
		'secondary',
		'secondary-white',
		'icon',
		'circle-icon',
		'small-button',
		'underline',
		'nav-button',
		'nav-top-button',
		'outline',
		'ghost',
	]),
	priBtnShowIcon: PropTypes.bool,
	priBtnClasses: PropTypes.string,
	priBtnOnClick: PropTypes.func,
	secBtn: PropTypes.string,
	secBtnType: PropTypes.string,
	secBtnVariant: PropTypes.oneOf([
		'primary',
		'primary-white',
		'secondary',
		'secondary-white',
		'icon',
		'circle-icon',
		'small-button',
		'underline',
		'nav-button',
		'nav-top-button',
		'outline',
		'ghost',
	]),
	secBtnShowIcon: PropTypes.bool,
	secBtnClasses: PropTypes.string,
	secBtnOnClick: PropTypes.func,
	info: PropTypes.shape({
		name: PropTypes.string.isRequired,
		value: PropTypes.string.isRequired,
	}),
	fullWidth: PropTypes.bool,
};

NotificationCard.defaultProps = {
	type: null,
	title: null,
	content: null,
	checkbox: null,
	link: null,
	priBtn: null,
	priBtnType: 'button',
	priBtnVariant: 'primary',
	priBtnShowIcon: false,
	priBtnOnClick: () => {},
	secBtn: null,
	secBtnType: 'button',
	secBtnVariant: 'secondary',
	secBtnShowIcon: false,
	secBtnOnClick: () => {},
	info: null,
	fullWidth: false,
};
